import * as E from 'fp-ts/Either';
import * as t from 'io-ts';
import {appendContext} from 'io-ts';

import {reportError} from './ioTsLogger';

export class TypeWithDefault<A, O, I> extends t.Type<A, O, I> {
  constructor(
    public type: t.Type<A, O, I>,
    defaultValue: A,
  ) {
    super(
      `Default${type.name}`,
      (value: unknown): value is A => type.is(value),
      (value, context) => {
        const result = type.validate(value, context);

        // If we succeeded, return the result.
        if (E.isRight(result)) {
          return result;
        }

        const fallbackMode = findFallbackMode(context);
        switch (fallbackMode) {
          case DefaultValueFallbackModesEnum.NO_FALLBACK:
            return result;

          case DefaultValueFallbackModesEnum.SILENT_FALLBACK:
            return t.success(defaultValue);

          case DefaultValueFallbackModesEnum.REPORT_FALLBACK:
          default: {
            const errors = result.left;
            errors.forEach((error) => {
              reportError('Fallback used', error, type.name);
            });

            return t.success(defaultValue);
          }
        }
      },
      (value) => type.encode(value),
    );
  }
}

// TODO: use `makeStrictModeType` instead of this once we're confident with the new approach
/** Build a custom runtime type with a default value in case of parsing failure. */
export function makeDefaultValueType<T extends t.Mixed>(
  type: T,
  defaultValue: t.TypeOf<T>,
): TypeWithDefault<t.TypeOf<T>, t.OutputOf<T>, t.InputOf<T>> {
  return new TypeWithDefault(type, defaultValue);
}

export enum DefaultValueFallbackModesEnum {
  NO_FALLBACK = 'NO_FALLBACK',
  SILENT_FALLBACK = 'SILENT_FALLBACK',
  REPORT_FALLBACK = 'REPORT_FALLBACK',
}

class FallbackModeType<A, O = A, I = unknown> extends t.Type<A, O, I> {
  constructor(type: t.Type<A, O, I>, fallbackMode: DefaultValueFallbackModesEnum) {
    super(
      `FallbackMode${type.name}`,
      type.is,
      (value, context) => type.validate(value, appendContext(context, type.name, type, value)),
      type.encode,
    );

    this.fallbackMode = fallbackMode;
  }

  public readonly fallbackMode: DefaultValueFallbackModesEnum;
}

export function makeDefaultValueFallbackModeType<A, O = A, I = unknown>(
  type: t.Type<A, O, I>,
  fallbackMode: DefaultValueFallbackModesEnum,
) {
  return new FallbackModeType(type, fallbackMode);
}

function isFallbackModeType<I, A>(type: t.Decoder<I, A>): type is FallbackModeType<A, A, I> {
  return type instanceof FallbackModeType;
}

function findFallbackMode(
  context: t.Context,
  defaultFallbackMode = DefaultValueFallbackModesEnum.REPORT_FALLBACK,
): DefaultValueFallbackModesEnum {
  const fallbackModeType = context.map((entry) => entry.type).find(isFallbackModeType);
  return fallbackModeType ? fallbackModeType.fallbackMode : defaultFallbackMode;
}
